import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import NetworkCanvas from '../components/landing/NetworkScene';

// ── Inline SVG icons ──────────────────────────────────────────────────────────
const ProtonIcon = ({ className = 'w-full h-full' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className={className}>
    <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
    <ellipse cx="12" cy="12" rx="10" ry="3.5" />
    <ellipse cx="12" cy="12" rx="10" ry="3.5" transform="rotate(60 12 12)" />
    <ellipse cx="12" cy="12" rx="10" ry="3.5" transform="rotate(-60 12 12)" />
  </svg>
);

const BrainIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
  </svg>
);

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);

const LinkIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
  </svg>
);

const BoltIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 inline-block ml-1">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

// ── Animation variants ────────────────────────────────────────────────────────
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

// ── Ticker data ───────────────────────────────────────────────────────────────
const TICKER_ITEMS = [
  { symbol: 'AAPL', price: '182.63', change: '+1.24%', up: true },
  { symbol: 'TSLA', price: '248.50', change: '-0.87%', up: false },
  { symbol: 'SPY',  price: '512.44', change: '+0.53%', up: true },
  { symbol: 'BTC',  price: '67,420', change: '+2.11%', up: true },
  { symbol: 'NVDA', price: '874.15', change: '+3.42%', up: true },
  { symbol: 'ETH',  price: '3,521',  change: '+1.77%', up: true },
  { symbol: 'AMZN', price: '185.70', change: '-0.32%', up: false },
  { symbol: 'MSFT', price: '415.32', change: '+0.91%', up: true },
];

// ── Strategies data ───────────────────────────────────────────────────────────
const STRATEGIES = [
  {
    name: 'Momentum',
    icon: '⚡',
    desc: 'Capture sustained price trends using RSI and volume signals. Rides market waves before reversals.',
    tags: ['Trend-Following', 'RSI', 'Volume'],
    color: 'from-neon/20 to-cyan-500/10',
    border: 'border-neon/30',
    badge: 'Active',
  },
  {
    name: 'Mean Reversion',
    icon: '↩',
    desc: 'Exploit statistical price deviations from moving averages. Buy the dip, sell the rip.',
    tags: ['Statistical', 'Bollinger Bands', 'Z-Score'],
    color: 'from-emerald-500/20 to-teal-500/10',
    border: 'border-emerald-500/30',
    badge: 'Active',
  },
  {
    name: 'Grid Trading',
    icon: '⊞',
    desc: 'Place layered buy/sell orders across a price range to profit from volatility in both directions.',
    tags: ['Market-Neutral', 'Automation', 'Range'],
    color: 'from-violet-500/20 to-purple-500/10',
    border: 'border-violet-500/30',
    badge: 'Active',
  },
  {
    name: 'Arbitrage',
    icon: '⇄',
    desc: 'Identify and exploit price discrepancies across exchanges and asset pairs with low latency execution.',
    tags: ['Cross-Exchange', 'Low Latency', 'Delta-Neutral'],
    color: 'from-amber-500/20 to-orange-500/10',
    border: 'border-amber-500/30',
    badge: 'Active',
  },
  {
    name: 'Market Making',
    icon: '⇕',
    desc: 'Provide liquidity by quoting bid/ask spreads. Earn consistent returns from the spread.',
    tags: ['Liquidity', 'Spread Capture', 'HFT'],
    color: 'from-pink-500/20 to-rose-500/10',
    border: 'border-pink-500/30',
    badge: 'Active',
  },
  {
    name: 'AI Signal Analysis',
    icon: '🧠',
    desc: 'Neural network-driven strategy combining macro economic indicators with real-time news sentiment.',
    tags: ['LLM-Powered', 'Macro/Micro', 'Sentiment'],
    color: 'from-neon/20 to-indigo-500/10',
    border: 'border-neon/30',
    badge: 'Coming Soon',
  },
];

// ── Integrations data ─────────────────────────────────────────────────────────
const INTEGRATIONS = [
  { name: 'Alpaca',               abbr: 'ALP',  live: true,  color: 'bg-yellow-400',     desc: 'Stocks & crypto via commission-free broker API' },
  { name: 'Charles Schwab',       abbr: 'SCH',  live: true,  color: 'bg-blue-600',       desc: 'Full-service broker with extended market access' },
  { name: 'Interactive Brokers',  abbr: 'IBKR', live: false, color: 'bg-red-600',        desc: 'Global markets, options & futures support' },
  { name: 'Coinbase',             abbr: 'CB',   live: false, color: 'bg-blue-500',       desc: 'Crypto-native exchange with DeFi gateway' },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: 'Features',     href: '#features' },
    { label: 'Strategies',   href: '#strategies' },
    { label: 'Integrations', href: '#integrations' },
    { label: 'Pricing',      href: '#pricing' },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background/95 backdrop-blur-md shadow-xl' : 'bg-transparent'
      }`}
    >
      {/* Ticker strip */}
      <TickerBar />

      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between border-t border-white/5">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-8 h-8 text-neon group-hover:text-green-400 transition-colors">
            <ProtonIcon />
          </div>
          <span className="text-foreground font-bold text-xl tracking-tight">Proton</span>
          <span className="text-neon/70 text-[10px] font-semibold uppercase tracking-[0.2em] ml-0.5 hidden sm:block">Trading</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="flex items-center gap-3">
          <Link
            to="/signin"
            className="hidden sm:inline-flex items-center px-4 py-2 rounded-lg border border-white/10 text-sm font-medium text-muted-foreground hover:text-foreground hover:border-white/25 transition-all"
          >
            Sign In
          </Link>
          <button
            className="md:hidden p-2 text-muted-foreground hover:text-foreground"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              {menuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              }
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-background/95 backdrop-blur-md border-b border-white/5 px-6 pb-4"
        >
          {navLinks.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              onClick={() => setMenuOpen(false)}
              className="block py-3 text-muted-foreground hover:text-foreground text-sm font-medium border-b border-white/5"
            >
              {label}
            </a>
          ))}
          <Link
            to="/signin"
            onClick={() => setMenuOpen(false)}
            className="block py-3 text-neon hover:text-green-400 text-sm font-semibold"
          >
            Sign In
          </Link>
        </motion.div>
      )}
    </header>
  );
}

// ── Ticker ────────────────────────────────────────────────────────────────────
function TickerBar() {
  const doubled = [...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div className="overflow-hidden py-2.5">
      <div
        className="flex gap-8 whitespace-nowrap animate-[ticker_28s_linear_infinite]"
      >
        {doubled.map((item, i) => (
          <span key={i} className="flex items-center gap-2 text-xs font-mono shrink-0">
            <span className="text-muted-foreground font-semibold">{item.symbol}</span>
            <span className="text-muted-foreground/70">{item.price}</span>
            <span className={item.up ? 'text-emerald-400' : 'text-red-400'}>{item.change}</span>
            <span className="text-muted-foreground/70 mx-1">|</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Gradient overlay for text readability over the fixed R3F background */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-background/30 via-transparent to-background/30" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-20 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-neon/30 bg-neon/10 text-neon text-xs font-semibold uppercase tracking-widest mb-8 backdrop-blur-sm"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-neon animate-pulse" />
          Now live on Alpaca Markets
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-foreground leading-tight tracking-tight"
        >
          Connections Fuel{' '}
          <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
            Intelligent
          </span>
          <br />
          Trading Systems.
        </motion.h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-6 max-w-2xl mx-auto text-lg sm:text-xl text-muted-foreground leading-relaxed"
        >
          Proton ingests real-time market events, broker signals, and macro data through a unified network —
          then deploys AI-driven strategies that adapt and execute autonomously.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Link
            to="/signup"
            className="group inline-flex items-center px-8 py-4 rounded-xl bg-neon text-black font-bold text-base transition-all hover:bg-neon/90 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-neon/20"
          >
            Start Trading Now
            <ArrowRightIcon />
          </Link>
          <a
            href="#how-it-works"
            className="inline-flex items-center px-8 py-4 rounded-xl border border-white/10 hover:border-white/25 text-muted-foreground hover:text-foreground font-semibold text-base transition-all backdrop-blur-sm"
          >
            How It Works
          </a>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 inline-flex flex-wrap justify-center gap-8 sm:gap-12 px-8 py-5 rounded-2xl bg-white/5 border border-white/8 backdrop-blur-md"
        >
          {[
            { value: '6+',       label: 'Strategies' },
            { value: 'Real-Time', label: 'Execution' },
            { value: 'Paper',    label: '& Live Modes' },
            { value: '4+',       label: 'Platforms Soon' },
          ].map(({ value, label }) => (
            <div key={label} className="text-center">
              <div className="text-2xl font-extrabold text-foreground">{value}</div>
              <div className="text-xs text-muted-foreground/70 mt-0.5 font-medium">{label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10"
      >
        <span className="text-muted-foreground/50 text-xs tracking-wider">Scroll to explore</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-5 h-5 text-muted-foreground/50"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </motion.div>
      </motion.div>
    </section>
  );
}

// ── Features ──────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: <BrainIcon />,
    title: 'AI-Powered Analysis',
    desc: 'Our engine ingests macro economic data, earnings feeds, and real-time news sentiment to surface high-probability setups before the market reacts.',
    color: 'text-neon',
    bg: 'bg-neon/10',
    highlight: 'Macro + Micro Signals',
  },
  {
    icon: <ChartIcon />,
    title: 'Quantitative Strategies',
    desc: 'From momentum and mean reversion to market making and arbitrage — deploy the same statistical edge used by professional quant desks.',
    color: 'text-violet-400',
    bg: 'bg-violet-400/10',
    highlight: '6 Core Strategies',
  },
  {
    icon: <LinkIcon />,
    title: 'Multi-Platform Execution',
    desc: 'Connected to Alpaca today with Schwab, Interactive Brokers, and Coinbase coming next. Trade stocks, options, and crypto through one interface.',
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
    highlight: '4+ Brokers Coming',
  },
  {
    icon: <ShieldIcon />,
    title: 'Built-In Risk Controls',
    desc: "Paper trading mode lets you refine strategies without real capital. Live mode includes position limits, drawdown guards, and an emergency stop.",
    color: 'text-amber-400',
    bg: 'bg-amber-400/10',
    highlight: 'Zero Surprise Exposure',
  },
];

function Features() {
  return (
    <section id="features" className="relative z-[1] py-28 bg-background/20">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="text-neon text-sm font-semibold uppercase tracking-widest mb-3">
            Platform Capabilities
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-4xl sm:text-5xl font-extrabold text-foreground">
            The edge you've been missing
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 max-w-xl mx-auto text-muted-foreground text-lg">
            Powerful tools built for precision — backed by data, not guesswork.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {FEATURES.map((f) => (
            <motion.div
              key={f.title}
              variants={fadeUp}
              className="relative group rounded-2xl border border-white/8 bg-white/[0.03] p-6 hover:border-white/15 hover:bg-white/[0.05] transition-all duration-300"
            >
              <div className={`w-11 h-11 ${f.bg} ${f.color} rounded-xl p-2.5 mb-5`}>
                {f.icon}
              </div>
              <div className={`inline-block text-xs font-bold ${f.color} uppercase tracking-wider mb-3`}>
                {f.highlight}
              </div>
              <h3 className="text-foreground font-bold text-lg mb-2">{f.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── How it works ──────────────────────────────────────────────────────────────
const STEPS = [
  {
    step: '01',
    title: 'Connect Your Broker',
    desc: 'Link your Alpaca account with an API key in under a minute. More brokers are coming soon — your credentials stay on your machine.',
    icon: <LinkIcon />,
    color: 'text-neon',
    bg: 'bg-neon/10',
  },
  {
    step: '02',
    title: 'Choose a Strategy',
    desc: 'Browse our strategy library, configure parameters like symbol, position size, and risk thresholds, then test safely in paper mode.',
    icon: <ChartIcon />,
    color: 'text-violet-400',
    bg: 'bg-violet-400/10',
  },
  {
    step: '03',
    title: 'Automate & Monitor',
    desc: 'Activate your strategy and let Proton handle execution. Track P&L, win rate, and Sharpe ratio in real time from your dashboard.',
    icon: <BoltIcon />,
    color: 'text-emerald-400',
    bg: 'bg-emerald-400/10',
  },
];

function HowItWorks() {
  return (
    <section id="how-it-works" className="relative z-[1] py-28">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="text-neon text-sm font-semibold uppercase tracking-widest mb-3">
            Getting Started
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-4xl sm:text-5xl font-extrabold text-foreground">
            Up and running in minutes
          </motion.h2>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="grid md:grid-cols-3 gap-8 relative"
        >
          <div className="hidden md:block absolute top-14 left-[calc(16.666%+2rem)] right-[calc(16.666%+2rem)] h-px bg-gradient-to-r from-neon/30 via-violet-500/30 to-emerald-500/30" />

          {STEPS.map((s) => (
            <motion.div key={s.step} variants={fadeUp} className="flex flex-col items-center text-center">
              <div className={`relative w-14 h-14 ${s.bg} ${s.color} rounded-2xl p-3.5 mb-5 shadow-lg`}>
                {s.icon}
                <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-card border border-white/10 text-foreground text-xs font-black flex items-center justify-center">
                  {s.step.slice(1)}
                </span>
              </div>
              <h3 className="text-foreground font-bold text-xl mb-3">{s.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── Strategies ────────────────────────────────────────────────────────────────
function Strategies() {
  return (
    <section id="strategies" className="relative z-[1] py-28 bg-card/20">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="text-neon text-sm font-semibold uppercase tracking-widest mb-3">
            Strategy Library
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-4xl sm:text-5xl font-extrabold text-foreground">
            Strategies that actually work
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 max-w-xl mx-auto text-muted-foreground text-lg">
            From classic quant techniques to cutting-edge AI — all configurable, all automatable.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {STRATEGIES.map((s) => (
            <motion.div
              key={s.name}
              variants={fadeUp}
              className={`relative rounded-2xl border ${s.border} bg-gradient-to-br ${s.color} p-6 hover:scale-[1.02] transition-transform duration-300 cursor-default`}
            >
              <span className={`absolute top-4 right-4 text-xs font-bold px-2.5 py-1 rounded-full ${
                s.badge === 'Active'
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
              }`}>
                {s.badge}
              </span>

              <div className="text-3xl mb-4 leading-none">{s.icon}</div>
              <h3 className="text-foreground font-bold text-xl mb-2">{s.name}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">{s.desc}</p>
              <div className="flex flex-wrap gap-2">
                {s.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-white/8 text-muted-foreground font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── Integrations ──────────────────────────────────────────────────────────────
function Integrations() {
  return (
    <section id="integrations" className="relative z-[1] py-28">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="text-neon text-sm font-semibold uppercase tracking-widest mb-3">
            Broker Integrations
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-4xl sm:text-5xl font-extrabold text-foreground">
            Your broker. Your choice.
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-4 max-w-xl mx-auto text-muted-foreground text-lg">
            Currently live on Alpaca — with major platforms coming soon so you never have to migrate.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {INTEGRATIONS.map((b) => (
            <motion.div
              key={b.name}
              variants={fadeUp}
              className={`relative rounded-2xl border p-6 text-center transition-all duration-300 ${
                b.live
                  ? 'border-emerald-500/40 bg-emerald-500/5 hover:border-emerald-500/60'
                  : 'border-white/8 bg-white/[0.03] opacity-70 hover:opacity-90'
              }`}
            >
              {b.live && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-0.5 rounded-full bg-emerald-500 text-white text-xs font-bold shadow-lg shadow-emerald-500/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Live
                </div>
              )}
              {!b.live && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-card text-muted-foreground text-xs font-semibold border border-white/10">
                  Coming Soon
                </div>
              )}

              <div className={`w-14 h-14 ${b.color} rounded-2xl flex items-center justify-center mx-auto mb-4 text-foreground font-black text-sm shadow-lg`}>
                {b.abbr}
              </div>
              <h3 className="text-foreground font-bold text-base mb-2">{b.name}</h3>
              <p className="text-muted-foreground text-xs leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ── Pricing ───────────────────────────────────────────────────────────────────
const CheckMark = ({ faded }) => (
  <svg viewBox="0 0 16 16" fill="none" className={`w-4 h-4 shrink-0 ${faded ? 'text-muted-foreground/50' : 'text-emerald-400'}`}>
    <circle cx="8" cy="8" r="7" className={faded ? 'fill-card' : 'fill-emerald-400/15'} />
    <path stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
      d={faded ? 'M5 8h6' : 'M5 8.5l2 2 4-4'} />
  </svg>
);

const TIERS = [
  {
    id: 'basic', name: 'Basic', price: null, priceLabel: 'Free',
    tagline: 'Start learning without risking a cent.', cta: 'Get started free', ctaTo: '/signup',
    highlight: false, badge: null, accent: 'border-white/10', accentText: 'text-muted-foreground',
    features: [
      { label: 'Paper trading only', on: true }, { label: '1 broker connection', on: true },
      { label: '3 strategies (run & maintain)', on: true }, { label: 'Core strategy library', on: true },
      { label: 'Live trading', on: false }, { label: 'Crypto trading', on: false },
      { label: 'Alerts', on: false }, { label: 'Bloomberg integration', on: false },
    ],
  },
  {
    id: 'core', name: 'Core', price: 20, priceLabel: '$20',
    tagline: 'Go live with crypto and smart alerts.', cta: 'Start Core', ctaTo: '/signup',
    highlight: false, badge: null, comingSoon: true, accent: 'border-neon/25', accentText: 'text-neon',
    features: [
      { label: 'Paper + live trading', on: true }, { label: '2 broker connections', on: true },
      { label: '10 strategies (maintain), 3 concurrent', on: true }, { label: 'Core strategy library', on: true },
      { label: 'Crypto trading', on: true }, { label: 'Price & strategy alerts', on: true },
      { label: 'Advanced analytics', on: false }, { label: 'Bloomberg integration', on: false },
    ],
  },
  {
    id: 'pro', name: 'Pro', price: 60, priceLabel: '$60',
    tagline: 'Scale your strategies like a professional desk.', cta: 'Start Pro', ctaTo: '/signup',
    highlight: true, badge: 'Most Popular', comingSoon: true, accent: 'border-neon/60', accentText: 'text-neon',
    features: [
      { label: 'Paper + live trading', on: true }, { label: '10 broker connections', on: true },
      { label: '100 strategies (maintain), 20 concurrent', on: true }, { label: 'Full strategy library + AI signals', on: true },
      { label: 'Crypto trading', on: true }, { label: 'Advanced alerts & notifications', on: true },
      { label: 'Advanced analytics dashboard', on: true }, { label: 'Bloomberg integration', on: false },
    ],
  },
  {
    id: 'plus', name: 'Plus', price: null, priceLabel: 'Custom',
    tagline: 'Unlimited scale with institutional-grade data.', cta: 'Contact us',
    ctaTo: 'mailto:sales@tradeproton.com', ctaExternal: true,
    highlight: false, badge: 'Enterprise', comingSoon: true, accent: 'border-violet-500/30', accentText: 'text-violet-300',
    features: [
      { label: 'Paper + live trading', on: true }, { label: 'Unlimited broker connections', on: true },
      { label: '1000 strategies (maintain & run)', on: true }, { label: 'Full strategy library + AI signals', on: true },
      { label: 'Crypto trading', on: true }, { label: 'Custom alerts & webhooks', on: true },
      { label: 'Advanced analytics dashboard', on: true }, { label: 'Bloomberg terminal integration', on: true },
    ],
  },
];

function Pricing() {
  return (
    <section id="pricing" className="relative z-[1] py-28 bg-background/20">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(34,197,94,0.05)_0%,transparent_65%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6">
        <motion.div
          variants={stagger} initial="hidden" whileInView="visible"
          viewport={{ once: true, amount: 0.2 }} className="text-center mb-16"
        >
          <motion.p variants={fadeUp} className="text-neon text-sm font-semibold uppercase tracking-widest mb-3">Pricing</motion.p>
          <motion.h2 variants={fadeUp} className="text-4xl sm:text-5xl font-extrabold text-foreground">Start free. Scale when ready.</motion.h2>
          <motion.p variants={fadeUp} className="mt-4 max-w-xl mx-auto text-muted-foreground text-lg">
            Every tier includes paper trading so you can build confidence before going live.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger} initial="hidden" whileInView="visible"
          viewport={{ once: true, amount: 0.05 }} className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6 items-start"
        >
          {TIERS.map((tier) => (
            <motion.div
              key={tier.id}
              variants={fadeUp}
              className={`relative flex flex-col rounded-2xl border ${tier.accent} p-6 transition-all duration-300
                ${tier.highlight
                  ? 'bg-gradient-to-b from-neon/10 to-card/80 scale-[1.03] xl:scale-[1.04]'
                  : tier.id === 'plus'
                    ? 'bg-gradient-to-b from-violet-500/8 to-card/60'
                    : 'bg-white/[0.03] hover:bg-white/[0.05]'
                }`}
            >
              {tier.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-xs font-bold whitespace-nowrap
                  ${tier.highlight ? 'bg-neon text-black' : 'bg-violet-600 text-foreground border border-violet-400/30'}`}>
                  {tier.badge}
                </div>
              )}

              <div className="mb-6 mt-2">
                <span className={`text-xs font-bold uppercase tracking-widest ${tier.accentText}`}>{tier.name}</span>
                <div className="mt-3 flex items-end gap-1">
                  <span className="text-4xl font-extrabold text-foreground leading-none">{tier.priceLabel}</span>
                  {tier.price !== null && <span className="text-muted-foreground/70 text-sm mb-0.5">/month</span>}
                </div>
                <p className="mt-2 text-muted-foreground text-sm leading-snug">{tier.tagline}</p>
              </div>

              <div className={`h-px mb-5 ${tier.highlight ? 'bg-neon/25' : 'bg-white/6'}`} />

              <ul className="flex-1 space-y-3 mb-7">
                {tier.features.map((f) => (
                  <li key={f.label} className="flex items-start gap-2.5">
                    <CheckMark faded={!f.on} />
                    <span className={`text-sm leading-snug ${f.on ? 'text-muted-foreground' : 'text-muted-foreground/50'}`}>{f.label}</span>
                  </li>
                ))}
              </ul>

              {tier.comingSoon ? (
                <div className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white/[0.04] border border-white/8 cursor-default">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  <span className="text-sm font-semibold text-muted-foreground/70">Coming Soon</span>
                </div>
              ) : tier.ctaExternal ? (
                <a href={tier.ctaTo}
                  className={`block w-full py-3 rounded-xl text-center text-sm font-bold transition-all
                    ${tier.id === 'plus'
                      ? 'bg-violet-600 hover:bg-violet-500 text-foreground shadow-lg shadow-violet-500/20'
                      : 'bg-white/8 hover:bg-white/12 text-foreground border border-white/10'}`}
                >{tier.cta}</a>
              ) : (
                <Link to={tier.ctaTo}
                  className={`block w-full py-3 rounded-xl text-center text-sm font-bold transition-all
                    ${tier.highlight
                      ? 'bg-neon text-black hover:bg-neon/90 hover:-translate-y-0.5'
                      : 'bg-white/8 hover:bg-white/12 text-foreground border border-white/10'}`}
                >{tier.cta}</Link>
              )}
            </motion.div>
          ))}
        </motion.div>

        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }}
          className="mt-10 text-center text-xs text-muted-foreground/50">
          All paid plans billed monthly. No long-term contracts. Cancel any time.
        </motion.p>
      </div>
    </section>
  );
}

// ── CTA Banner ────────────────────────────────────────────────────────────────
function CTABanner() {
  const BULLETS = [
    'No trading experience required',
    'Paper mode — zero risk to start',
    'Real-time performance analytics',
    'Full API transparency',
  ];

  return (
    <section className="relative z-[1] py-28 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(34,197,94,0.08)_0%,transparent_70%)] pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-6 text-center">
        <motion.div
          variants={stagger} initial="hidden" whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div variants={fadeUp} className="w-16 h-16 text-neon mx-auto mb-6">
            <ProtonIcon />
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-4xl sm:text-5xl font-extrabold text-foreground mb-4">
            Ready to trade smarter?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-lg max-w-xl mx-auto mb-10">
            Join traders who are automating their edge with Proton. Start with paper trading and go live when you're ready.
          </motion.p>

          <motion.ul variants={stagger} className="flex flex-wrap justify-center gap-x-8 gap-y-3 mb-12">
            {BULLETS.map((b) => (
              <motion.li key={b} variants={fadeUp} className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                <span className="text-emerald-400"><CheckIcon /></span>
                {b}
              </motion.li>
            ))}
          </motion.ul>

          <motion.div variants={fadeUp}>
            <Link
              to="/signup"
              className="inline-flex items-center px-10 py-5 rounded-xl bg-neon text-black hover:bg-neon/90 font-bold text-lg transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-neon/20"
            >
              Create Free Account
              <ArrowRightIcon />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="relative z-[1] bg-background/30 border-t border-white/5 py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 text-neon">
              <ProtonIcon />
            </div>
            <span className="text-foreground font-bold">Proton Trading</span>
          </div>

          <p className="text-muted-foreground/50 text-xs text-center max-w-md">
            This software is for informational purposes only. Trading involves substantial risk of loss.
            Past performance does not guarantee future results. Not financial advice.
          </p>

          <div className="flex gap-6">
            {['Features', 'Strategies', 'Integrations'].map((label) => (
              <a key={label} href={`#${label.toLowerCase()}`}
                className="text-muted-foreground/70 hover:text-muted-foreground text-sm transition-colors">
                {label}
              </a>
            ))}
            <Link to="/signin" className="text-muted-foreground/70 hover:text-neon text-sm transition-colors font-semibold">
              Sign In
            </Link>
            <a href="/terms" className="text-muted-foreground hover:text-neon transition-colors text-sm">Terms of Service</a>
            <a href="/privacy" className="text-muted-foreground hover:text-neon transition-colors text-sm">Privacy Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const vh = window.innerHeight || 1;
      setScrollProgress(Math.min(window.scrollY / vh, 1));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Persistent R3F background — fixed behind all content */}
      <div className="fixed inset-0 z-0">
        <NetworkCanvas scrollProgress={scrollProgress} className="w-full h-full" />
      </div>

      <Navbar />
      {/* Spacer = ticker (~34px) + nav bar (64px) */}
      <div className="h-[98px]" />
      <Hero />
      <Features />
      <HowItWorks />
      <Strategies />
      <Integrations />
      <Pricing />
      <CTABanner />
      <Footer />
    </div>
  );
}
